import React, {
    useContext, useEffect, useCallback, useState, useMemo
} from 'react';
import _ from 'lodash';
import { readViewModelValue } from 'gw-jutro-adapters-react';
import { WizardPage, wizardProps } from 'gw-portals-wizard-react';
import {
    ViewModelServiceContext,
    withViewModelService,
    ViewModelForm
} from 'gw-portals-viewmodel-react';
import { useHistory } from 'react-router-dom';
import { useValidation } from 'gw-portals-validation-react';
import { Claim, Address } from 'gw-capability-fnol-common-react';
import { FormattedMessage, useTranslator } from '@jutro/locale';
import { useModal } from '@jutro/components';
import AdacDigitalWizardPageTemplate from '../../templates/AdacDigitalWizardPageTemplate';
import FNOLContext from '../../components/Context/Context';
import Person from '../../components/Person/Person';
import metadata from './AdacContactDetailsPage.metadata.json5';
import wizardMessages from '../../FNOLAdacWizard.messages';
import messages from './AdacContactDetailsPage.messages';
import styles from './AdacContactDetailsPage.module.scss';
import { PATIENT_ROLE_CODE, INSURED_ROLE_CODE } from '../../constants/RoleCodes';
import ACTION_OPTIONS from '../../constants/FnolActionOptions';

function AdacContactDetailsPage(props) {
    const viewModelService = useContext(ViewModelServiceContext);
    const {
        wizardData: claimVM, updateWizardData
    } = props;
    const history = useHistory();
    const translator = useTranslator();
    const fnolContext = useContext(FNOLContext);
    const { actionOption, agreementConsent, setAgreementConsent } = fnolContext;
    const [isContactNeeded, setIsContactNeeded] = useState(claimVM.value.contacts.length >= 2);
    const [memNumClasses, setMemNumClasses] = useState(styles.validInputWithLabel);
    const { showConfirm } = useModal();

    const {
        onValidate,
        isComponentValid,
        initialValidation,
        disregardFieldValidation
    } = useValidation('FNOLContactDetailsPage');

    const handleAgreementSelect = useCallback((value) => {
        setAgreementConsent(value);
    }, [setAgreementConsent]);

    const createConsentVM = useCallback((type, approved) => {
        const consentVM = viewModelService.create(
            {
                type: type,
                isApproved: approved
            },
            'cc',
            'adac.edge.capabilities.claim.fnol.consent.dto.ConsentDTO'
        );
        return consentVM;
    }, [viewModelService]);

    const initializeVM = useCallback(() => {
        const submittingClaimVM = viewModelService.create(
            new Claim(claimVM.value),
            'cc',
            'edge.capabilities.claim.fnol.dto.FnolDTO'
        );
        const newClaimVM = viewModelService.clone(submittingClaimVM);
        updateWizardData(newClaimVM);
        return newClaimVM;
    }, [claimVM, updateWizardData, viewModelService]);

    const sortCountryName = useMemo(() => {
        if (!viewModelService) return [];
        const typelist = viewModelService.productMetadata.get('cc').types.getTypelist('typekey.Country');
        const countryTypeModel = typelist.codes
            .map((typecode) => ({
                ...typecode,
                name: translator({
                    id: `typekey.Country.${typecode.code}`,
                    defaultMessage: typecode.name
                })
            }));
        return countryTypeModel.sort((a, b) => {
            return a.name.localeCompare(b.name);
        });
    }, [viewModelService, translator]);

    useEffect(
        () => {
            const newClaimVM = initializeVM();
            if (_.isEmpty(claimVM.value.contacts)) {
                newClaimVM.value.createContact({
                    policyRole: INSURED_ROLE_CODE,
                    primaryPhoneType: 'mobile'
                });
            }
            if (_.isEmpty(newClaimVM.value.lossLocation)) {
                newClaimVM.value.lossLocation = new Address();
                newClaimVM.value.lossLocation.addressType = 'other';
            }
            if (_.isEmpty(newClaimVM.value.lossCause) && actionOption !== ACTION_OPTIONS.option1) {
                const lossCauseTypesArray = newClaimVM.lossCause.aspects.availableValues;
                const LossCause = _.find(lossCauseTypesArray, (cause) => {
                    return cause.code === 'consulting_information';
                });
                _.set(newClaimVM, 'lossCause.value', LossCause);
            }
        },
        // execute once
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const setUpConsents = useCallback((claim, role) => {
        const gdprConsent = createConsentVM('gdpr', agreementConsent);
        const electronicSignature = createConsentVM('electronic_signature', agreementConsent);
        const updatedContacts = claim.value.contacts.map((c) => (c.policyRole === role
            ? { ...c, consents_Adac: [gdprConsent.value, electronicSignature.value] } : c));
        _.set(claim, 'contacts', updatedContacts);
    }, [createConsentVM, agreementConsent]);

    const onNext = useCallback(() => {
        setUpConsents(claimVM, INSURED_ROLE_CODE);
        return claimVM;
    }, [claimVM, setUpConsents]);

    const handlePatientPersonSelect = useCallback(() => {
        if (claimVM.value.contacts.length < 2) {
            claimVM.value.createContact({
                policyRole: PATIENT_ROLE_CODE,
                primaryPhoneType: 'mobile'
            });
        } else {
            _.remove(claimVM.value.contacts,
                (c) => c.policyRole === PATIENT_ROLE_CODE);
            disregardFieldValidation('patientPerson');
        }
        setIsContactNeeded(!isContactNeeded);
    }, [claimVM.value, disregardFieldValidation, isContactNeeded]);

    const writeValue = useCallback((value, path) => {
        _.set(claimVM, path, value);
        updateWizardData(claimVM);
    }, [claimVM, updateWizardData]);

    const getContactIndex = useCallback((code) => {
        const contacts = _.get(claimVM.value, 'contacts');
        return contacts.indexOf(contacts.find((c) => c.policyRole === code));
    }, [claimVM.value]);

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            handlePatientPersonSelect,
            handleAgreementSelect
        },
        resolveComponentMap: {
            person: Person
        }
    };

    const overrides = {
        '@field': {
            phoneWide: {
                labelPosition: 'top'
            }
        },
        insured: {
            path: `contacts.children[${getContactIndex(INSURED_ROLE_CODE)}]`,
            onChange: writeValue,
            onValidate,
            isOtherIncidentNoteVisible : actionOption !== ACTION_OPTIONS.option1
        },
        patientPersonSelect: {
            value: isContactNeeded
        },
        patientPersonSelectContainer: {
            visible: actionOption === ACTION_OPTIONS.option1
        },
        patientPersonContainer: {
            visible: isContactNeeded
        },
        patientPerson: {
            path: `contacts.children[${getContactIndex(PATIENT_ROLE_CODE)}]`,
            onChange: writeValue,
            onValidate,
        },
        travelCountry: {
            availableValues: sortCountryName,
            label: translator(messages.travelCountry),
            hideLabel: _.isEmpty(_.get(claimVM.value, 'lossLocation.country')),
            className: _.isEmpty(_.get(claimVM.value, 'lossLocation.country')) ? undefined : styles.dropdownWithLabel
        },
        travelCity: {
            label: translator(messages.travelCity),
            hideLabel: _.isEmpty(_.get(claimVM.value, 'lossLocation.city')),
            className: _.isEmpty(_.get(claimVM.value, 'lossLocation.city')) ? undefined : styles.validInputWithLabel
        },
        adacMembershipNumberField: {
            label: translator(messages.adacMembershipNumber),
            hideLabel: _.isEmpty(_.get(claimVM.value, 'policy.policyNumber')),
            className: _.isEmpty(_.get(claimVM.value, 'policy.policyNumber')) ? undefined : memNumClasses,
            onBlur: () => {
                if (!_.get(claimVM, 'policy.policyNumber.aspects.valid') && !_.isEmpty(_.get(claimVM.value, 'policy.policyNumber'))) {
                    setMemNumClasses(styles.invalidInputWithLabel);
                } else {
                    setMemNumClasses(styles.validInputWithLabel);
                }
            }
        },
        agreementText: {
            content: <FormattedMessage
                {...messages.furtherStepsConsent}
                values={{
                    dataProtection: (
                        <a
                            className={styles.link}
                            href={translator(messages.dataProtectionLink)}
                            target="_blank"
                            rel="noreferrer"
                        >
                            {`${translator(messages.dataProtection)}`}
                        </a>
                    ),
                    termsOfUse: (
                        <a
                            className={styles.link}
                            href={translator(messages.termsOfUseLink)}
                            target="_blank"
                            rel="noreferrer"
                        >
                            {`${translator(messages.termsOfUse)}`}
                        </a>
                    )
                }}
            />
        },
        agreement: {
            value: agreementConsent
        }
    };

    const readValue = useCallback(
        (id, path) => {
            return readViewModelValue(metadata.pageContent, claimVM, id, path, overrides);
        },
        [claimVM, overrides]
    );

    return (
        <WizardPage
            onNext={onNext}
            showCancel={false}
            showPrevious={false}
            nextLabel={wizardMessages.next}
            disableNext={!isComponentValid || !agreementConsent}
            skipWhen={initialValidation}
            template={AdacDigitalWizardPageTemplate}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={claimVM}
                resolveValue={readValue}
                classNameMap={resolvers.resolveClassNameMap}
                callbackMap={resolvers.resolveCallbackMap}
                componentMap={resolvers.resolveComponentMap}
                overrideProps={overrides}
                onModelChange={updateWizardData}
                onValidationChange={onValidate}
            />
        </WizardPage>
    );
}

AdacContactDetailsPage.propTypes = wizardProps;
export default withViewModelService(AdacContactDetailsPage);
